import React, { useCallback, useEffect } from "react";
import Modal from "../../../../Components/Atoms/Modal";
import { useTransactionStore } from "../../../../Store/Transaction";
import { useAuth0 } from "@auth0/auth0-react";
import Table, { Column, DataSourceItem } from "../../../../Components/Atoms/Table";
import Tag from "../../../../Components/Atoms/Tag";
import {
  AdyenTransactionStatus,
  getPaymentMethodDetails,
  Transaction
} from "../../../../Contracts/Transaction";
import { useTeamStore } from "../../../../Store/Team";
import TablePaginationFooter from "../../../../Components/Atoms/Table/TablePaginationFooter";
import { PAGINATION_KEYS, usePagination } from "../../../../Store/Pagination";
import LoadingPage from "../../../LoadingPage";

export interface UnmappedCustomersTransactionsListModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerId?: number;
}

const UnmappedCustomersTransactionsListModal: React.FC<
  UnmappedCustomersTransactionsListModalProps
> = ({ isOpen, onClose, customerId }) => {
  const { limit, page, setLimit, setPage } = usePagination(
    PAGINATION_KEYS.UNMAPPED_CUSTOMERS_TRANSACTIONS_MODAL,
    { limit: 10, page: 1 }
  );

  const { getTransactions, resetTransactions, transactions, transactionsCount, isLoading } =
    useTransactionStore();

  const { getAccessTokenSilently } = useAuth0();
  const { formatCurrency } = useTeamStore();

  const refreshData = useCallback(async () => {
    if (customerId) {
      await getTransactions(
        getAccessTokenSilently,
        limit,
        undefined,
        page,
        `customer_id:eq:${customerId}`,
        undefined
      );
    }
  }, [customerId, getTransactions, getAccessTokenSilently, limit, page]);

  useEffect(() => {
    if (!isOpen) {
      resetTransactions();
    } else {
      refreshData();
    }
  }, [isOpen, resetTransactions, refreshData]);

  const columns: Column<Transaction>[] = [
    {
      key: "transaction_id",
      title: "Transaction Id",
      dataIndex: "transaction_id"
    },
    {
      key: "quote_amount",
      title: "Quote Amount",
      dataIndex: "quote_amount"
    },
    {
      key: "quote_id",
      title: "Quote Id",
      dataIndex: "quote_id"
    },
    {
      key: "payment_method",
      title: "Payment Method",
      dataIndex: "payment_method",
      render: d => {
        const details = getPaymentMethodDetails(d.data.payment_method);
        return (
          <div className="flex items-center space-x-1">
            <img src={details.logo} alt={details.name} className="h-8" />
            <div>{d.data.payment_method_summary}</div>
          </div>
        );
      }
    },
    {
      key: "transaction_status",
      title: "Transaction Status",
      dataIndex: "transaction_status",
      render: d => (
        <Tag
          color={
            d.data.status === AdyenTransactionStatus.Completed
              ? "lime"
              : d.data.status === AdyenTransactionStatus.Processing
                ? "yellow"
                : d.data.status === AdyenTransactionStatus.Refunded
                  ? "gray"
                  : d.data.status === AdyenTransactionStatus.Failed ||
                      d.data.status === AdyenTransactionStatus.Chargeback
                    ? "red"
                    : d.data.status === AdyenTransactionStatus.RefundInitiated
                      ? "blue"
                      : "gray"
          }
        >
          {d.data.status}
        </Tag>
      )
    }
  ];

  const dataSource: DataSourceItem<Transaction>[] = transactions.map(t => ({
    key: t.id.toString(),
    transaction_id: t.psp_reference,
    quote_amount: formatCurrency(t.total_amount),
    quote_id: t.transaction_identifier,
    payment_method: t.payment_method,
    transaction_status: t.status,
    data: t
  }));

  return (
    <Modal title="Transactions" isOpen={isOpen} onClose={onClose} footer={null} topOffset="7rem">
      <div className="flex flex-col h-[30rem] w-[50rem]">
        <div className="flex flex-col flex-grow overflow-y-auto">
          {isLoading && transactions.length === 0 ? (
            <div className="my-auto">
              <LoadingPage />
            </div>
          ) : (
            <Table columns={columns} dataSource={dataSource} isLoading={isLoading} />
          )}
          {!isLoading && transactions.length === 0 && (
            <div className="text-center text-gray-400 mt-12">No transactions found</div>
          )}
        </div>
        <TablePaginationFooter
          limit={limit}
          setLimit={setLimit}
          page={page}
          setPage={setPage}
          totalRecords={transactionsCount}
        />
      </div>
    </Modal>
  );
};

export default UnmappedCustomersTransactionsListModal;
